import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

import { secondaryMain, primaryLightest } from '../../../constants/colors';
import SakuraCard from '../../../common/layout/SakuraCard';
import { SubmitButton } from '../../../common/forms/buttons';
import { NumberInput } from '../fields';

const axios = require('axios').default;

const useStyles = makeStyles((theme) => ({
  card: {
    padding: '12px 24px',
  },
  cardTitle: {
    marginBottom: 12,
    fontSize: 12,
    color: secondaryMain,
  },
  error: {
    color: 'red',
    fontWeight: 'bold',
  },
  success: {
    color: 'seagreen',
    fontWeight: 'bold',
  },
  formControl: {
    width: '100%',
    marginBottom: 16,
  },
  selectLabel: {
    fontWeight: 'bold',
    paddingLeft: 14,
    marginBottom: 4,
  },
  select: {
    background: primaryLightest,
    padding: '10px 14px',
  },
  checkboxLabel: {
    fontWeight: 'bold',
  },
}));

export default function AdmConfig(props) {
  const classes = useStyles();
  const { release, jwtToken, baseApiUri } = props;

  const getUrl = (r) => `${baseApiUri}/releases/${r.id}/config/adm`;

  // -------------------------------- State -------------------------------- //
  // ----------------------------------------------------------------------- //
  const [config, setConfig] = React.useState();
  const [errorMessage, setErrorMessage] = React.useState();
  const [successMessage, setSuccessMessage] = React.useState();
  const [disabled, setDisabled] = React.useState(true);

  // --------------------------- Lifecycle Hooks --------------------------- //
  // ----------------------------------------------------------------------- //
  React.useEffect(() => {
    if (!release) return;

    setConfig(undefined);
    setErrorMessage(undefined);
    setSuccessMessage(undefined);

    const headers = { Authorization: jwtToken };

    return axios.get(getUrl(release), { headers })
      .then(response => {
        setConfig(response.data);
        setDisabled(false);
      })
      .catch(err => {
        console.log(err);
      })
  }, [release, jwtToken, baseApiUri]);

  // --------------------------- Event Handlers ---------------------------- //
  // ----------------------------------------------------------------------- //
  const handleChange = (field) => (e) => setConfig({
    ...config,
    [field]: Number(e.target.value),
  });

  const handleFilterModeChange = (e) => setConfig({
    ...config,
    filter_mode: e.target.value,
  });

  const handleBooleanChange = (field) => (e) => setConfig({
    ...config,
    [field]: e.target.checked,
  });

  const handleSubmit = (e) => {
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    setDisabled(true);

    const headers = { Authorization: jwtToken };

    return axios.put(getUrl(release), config, {headers})
      .then(response => {
        setConfig(response.data);
        setDisabled(false);
        setSuccessMessage('Config updated successfully.');
      })
      .catch(err => {
        setErrorMessage('Could not update configuration.');
        setDisabled(false);
      })
  };

  // ------------------------------- Render -------------------------------- //
  // ----------------------------------------------------------------------- //
  const fields = () => [
    { label: 'Start Accuracy', value: config.start_accuracy, onChange: handleChange('start_accuracy') },
    { label: 'Final Accuracy', value: config.final_accuracy, onChange: handleChange('final_accuracy') },
    { label: 'Accuracy Increment', value: config.accuracy_increment, onChange: handleChange('accuracy_increment') },
    { label: 'Inflection Points', value: config.inflection_points, onChange: handleChange('inflection_points') },
    { label: 'Slope Relative Change', value: config.slope_relative_change, onChange: handleChange('slope_relative_change') },
    { label: 'Min Data Points Required', value: config.min_data_points_required, onChange: handleChange('min_data_points_required') },
    { label: 'Stability Min Points', value: config.stability_min_points, onChange: handleChange('stability_min_points') },
  ];

  return (
    <form onSubmit={ (e) => { handleSubmit(); e.preventDefault(); } }>
      { config && (
        <SakuraCard title='ADM Configuration'>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography className={classes.cardTitle}>ADM Config</Typography>

              { fields().map((field) => <NumberInput disabled={disabled} required={true} {...field} key={field.label} />) }

              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel className={classes.selectLabel} shrink={true}>Filter Mode</InputLabel>
                <Select
                  value={config.filter_mode || 'none'}
                  onChange={handleFilterModeChange}
                  disabled={disabled}
                  className={classes.select}
                  required
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="active_days">Active Days</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.enable_stability || false}
                    onChange={handleBooleanChange('enable_stability')}
                    disabled={disabled}
                    color="primary"
                  />
                }
                label={<span className={classes.checkboxLabel}>Enable Stability</span>}
              />
            </Grid>
          </Grid>

          <br/>
          <Divider />
          <br/>

          <Typography className={classes.error} align='center'>{errorMessage}</Typography>
          <Typography className={classes.success} align='center'>{successMessage}</Typography>

          <br/>

          <Box display="flex" justifyContent="center" alignItems="center">
            <SubmitButton disabled={disabled} />
          </Box>
        </SakuraCard>
      )}
    </form>
  )
}
